import uaParser from 'ua-parser-js';
import { isObject } from 'lodash';
import { logger } from '@fiverr-private/obs';
import { device } from '../../../lib';

/**
 * The computed mark symbol, used to indicate whether a given target was already computed or not.
 * @type {Symbol}
 */
const computedSymbol = Symbol();

/**
 * Defines a given property and value over a given instance in a nun enumerable way.
 * @param {Partial<FiverrContext>} instance - The instance to assign the computed property to.
 * @param {String|Symbol} property - The property name.
 * @param {*} value - The computed property value.
 */
const defineComputed = (instance, property, value) => {
    Object.defineProperty(instance, property, {
        value,
        writable: false,
        enumerable: false
    });
};

/**
 * Validates a given target is assignable.
 * @param {*} target - The target to validate.
 * @return {Boolean}
 */
const validateTarget = (target) => !!target
    && isObject(target)
    && !Array.isArray(target);

/**
 * Sets the computed FiverrContext properties mixin upon a context target.
 * @param {FiverrContext} target - The target to assign the computed mixin.
 * @return {FiverrContext}
 */
const computed = (target) => {
    if (target[computedSymbol]) { return target; }

    if (!validateTarget(target)) {
        logger.error('FiverrContext computed mixin target is not valid, please contact platform team.');
        return target;
    }

    const { userAgent } = target;

    const userAgentData = uaParser(userAgent);
    const { device: { type }, os: { name } } = userAgentData;

    const computed = {
        isMobile: device.isMobile(type),
        isTouch: device.isTouch(type),
        isIos: device.isIos(name),
        isAndroid: device.isAndroid(name)
    };

    defineComputed(target, computedSymbol, true);

    Object.entries(computed)
        .forEach(([name, value]) => defineComputed(
            target,
            name,
            value
        ));

    return target;
};

export {
    computed
};
